import Victor from 'victor'
import { bisector, max } from 'd3-array'

import { ScaleLinear } from 'd3-scale'
import { DataTrajectoryPoint, TrajectoryPoint, GammaResistivity, TrajectoryProblem } from '../types'
import loggerFactory from 'live/services/logger'
import paper from 'paper'
import { equation, intersection } from './SegmentTree'

const logger = loggerFactory('wbs-chart-trajectory')

interface Point extends DataTrajectoryPoint {
    point: Victor
    vector: Victor
}

interface LinearFunc {
    slope: number
    b: number
}

interface AvailableDistance {
    startMd: number
    endMd: number
    distance: number
}

export type DepthType = 'tvd' | 'md'

export function getLinearFunc(v1: Victor, v2: Victor): LinearFunc {
    const slope = (v2.y - v1.y) / (v2.x - v1.x)
    const b = (v1.y / slope) * v1.x

    return {
        slope,
        b
    }
}

export default class Trajectory {
    private points: Point[] = []
    private problems: TrajectoryProblem[] = []
    private readonly simplifiedTrajectory: paper.Path
    private readonly availableDistance: number
    private availableDistances: AvailableDistance[]
    private readonly startMd: number
    private readonly resampleRate: number

    constructor(trajectory: DataTrajectoryPoint[], depthUnit: string) {
        const points = trajectory.filter(p => p.vertSect != null && p.tvd != null)
        if (trajectory.length !== points.length) {
            logger.warn('detected (and ignored) trajectory points without vertSect or tvd', trajectory)
        }

        if (points.length < 1) return

        if (points[0].md > 0) {
            points.unshift({
                azi: 0,
                dispEw: 0,
                dispNs: 0,
                dls: 0,
                incl: 0,
                md: 0,
                timestamp: 0,
                tvd: 0,
                vertSect: 0
            })
            logger.warn('added trajectory point at origin')
        }

        this.resampleRate = depthUnit === 'ft' ? 25 * 3.28084 : 25
        this.startMd = points[0].md
        this.simplifiedTrajectory = this.simplifyTrajectory(points)
        this.availableDistance = this.evaluateTrajectory()
        console.debug('trajectory available distance', this.availableDistance)

        for (let i = 0; i < points.length; i++) {
            const currentPoint = points[i]
            const lastPoint = this.points[this.points.length - 1]
            // duplicate points can break some functions of the WBS so we need to check if the current is equal to the last
            if (currentPoint.md !== lastPoint?.md) {
                // vertsect cant be zero otherwise the tooltip calculation will break
                const vertSectFailSafe = 0.001
                const victorPoint = new Victor(currentPoint.vertSect || vertSectFailSafe, currentPoint.tvd)

                // calculate the normal vector.
                let vector
                if (i === 0) {
                    // we need to get the next value after the first point that isn't a duplicate
                    const p1 = points.find(el => el.md !== currentPoint.md)
                    vector = new Victor(p1.vertSect, p1.tvd).clone().subtract(victorPoint).rotateDeg(-90).normalize()
                    // when there are duplicates the value of the last point might not be the last point, so we need to check if the current has the same value as the last point
                } else if (currentPoint.md === points[points.length - 1].md) {
                    vector = victorPoint.clone().subtract(lastPoint.point).rotateDeg(-90).normalize()
                } else {
                    vector = victorPoint
                        .clone()
                        .subtract(lastPoint.point)
                        .rotateDeg(-90)
                        .add(lastPoint.vector)
                        .normalize()
                }
                this.points.push({
                    point: victorPoint,
                    vector,
                    ...currentPoint
                })
            }
        }
    }

    private simplifyTrajectory(points: DataTrajectoryPoint[]): paper.Path {
        // paper.js without canvas
        paper.setup(new paper.Size(1, 1))

        const segments = points.map(p => new paper.Point(p.vertSect, p.tvd))
        const path = new paper.Path(segments)

        if (!path.simplify()) {
            console.error('trajectory was not simplified')
        }

        return path
    }

    private evaluateTrajectory(): number {
        const mdSampled = this.resampleMd(this.startMd, this.simplifiedTrajectory.length)

        this.availableDistances = []
        let f1
        let lastMd
        let availableDistance = Number.POSITIVE_INFINITY
        mdSampled.forEach(md => {
            const trajectoryPoint = this.simplifiedTrajectory.getPointAt(md)
            const normal = this.simplifiedTrajectory.getNormalAt(md)

            const f2 = equation(trajectoryPoint, trajectoryPoint.subtract(normal))
            if (f1) {
                const intersectionPoint = intersection(f1, f2)
                const distance = new paper.Point(intersectionPoint.x, intersectionPoint.y).subtract(trajectoryPoint)
                    .length

                this.availableDistances.push({
                    startMd: lastMd,
                    endMd: md,
                    distance: distance
                })

                if (distance < availableDistance) {
                    availableDistance = distance
                }
            }

            f1 = f2
            lastMd = md
        })

        return availableDistance
    }

    getAvailableDistance(): number {
        return this.availableDistance
    }

    public setProblems(problems: TrajectoryProblem[]): void {
        this.problems = problems
    }

    public getProblems(): TrajectoryProblem[] {
        return this.problems
    }

    public hasProblems(): boolean {
        return this.problems.length > 0
    }

    getPolygons(): TrajectoryPoint[][] {
        const polygons = []
        for (let i = 1; i < this.points.length; i++) {
            const polygon = []
            const previous = this.points[i - 1]
            polygon.push(this.getPoint(previous.md, 500))
            polygon.push(this.getPoint(this.points[i].md, 500))
            polygon.push(this.getPoint(this.points[i].md, -500))
            polygon.push(this.getPoint(previous.md, -500))
            polygons.push(polygon)
        }

        return polygons
    }

    get maxMD(): number {
        return this.points.length ? this.points[this.points.length - 1].md : 0
    }

    get minMD(): number {
        return this.points.length ? this.points[0].md : 0
    }

    maxDepth(depthType: DepthType): number {
        return this.points[this.points.length - 1][depthType]
    }

    getAllPoints(): Point[] {
        return this.points
    }

    public getPoints(): Victor[] {
        return this.points.map(p => p.point.clone())
    }

    public hasPoints(): boolean {
        return this.points.length > 0
    }

    public getLimits(): [number, number] {
        const xLimits = max(this.points, v => Math.abs(v.point.x)) as number
        const yLimits = max(this.points, v => Math.abs(v.point.y)) as number

        return [xLimits, yLimits]
    }

    getVectors(): Victor[][] {
        return this.points.map(e => {
            const point = e.point.clone()
            const vector = e.vector.clone()

            return [
                point.clone().add(vector.clone().multiplyScalar(-500)),
                point.clone().add(vector.clone().multiplyScalar(500))
            ]
        })
    }

    getNextPointPosition(tvd: number, depthType: DepthType): number {
        const bisectt = bisector((p: Point) => p[depthType]).right
        const pos = bisectt(this.points, tvd)
        if (pos <= 0) {
            return 1
        } else if (pos >= this.points.length) {
            return this.points.length - 1
        } else {
            return pos
        }
    }

    // TODO: go everywhere this func is used and add the depthType param
    getPoint(depth: number, offset: number, depthType: DepthType = 'md'): TrajectoryPoint {
        if (!this.points.length) {
            return {
                x: 0,
                y: 0,
                vector: { x: 0, y: 0 },
                [depthType]: depth,
                invalid: true
            }
        }

        if (depthType == 'md') {
            return this.getProjectedPoint(depth, offset)
        }

        const pos = this.getNextPointPosition(depth, depthType)

        const depthStart = this.points[pos - 1][depthType]
        const depthEnd = this.points[pos][depthType]
        const invalid = this.problems.find(p => p.index === pos || p.index === pos - 1) !== undefined

        const distance = depthEnd - depthStart

        const percent = (depth - depthStart) / distance

        const vector = this.points[pos - 1].vector.clone().add(this.points[pos].vector).normalize()

        if (percent === 0) {
            const p = this.points[pos - 1].point.clone().add(this.points[pos - 1].vector.clone().multiplyScalar(offset))

            return {
                x: Math.round(p.x),
                y: Math.round(p.y),
                vector,
                [depthType]: depth,
                invalid
            }
        }

        const p1 = this.points[pos - 1].point.clone()
        const p2 = this.points[pos].point.clone()

        if (offset !== 0 && depthType !== 'tvd') {
            p1.add(this.points[pos - 1].vector.clone().multiplyScalar(offset))
            p2.add(this.points[pos].vector.clone().multiplyScalar(offset))
        }

        const newPoint = p1.mix(p2, percent)

        return {
            [depthType]: depth,
            x: newPoint.x,
            y: newPoint.y,
            vector,
            invalid
        }
    }

    private resampleMd(mdStart: number, mdEnd: number): number[] {
        const start = Math.max(this.startMd, mdStart)
        const stop = Math.min(mdEnd, this.simplifiedTrajectory.length)

        const mds = Array.from(
            { length: (stop - start) / this.resampleRate + 1 },
            (_, i) => start + i * this.resampleRate
        )

        const last = mds[mds.length - 1]
        if (last < stop) {
            mds.push(stop)
        }

        return mds
    }

    getProjectedPoint(md: number, offset: number): TrajectoryPoint {
        let depth = Math.max(this.startMd, md)
        depth = Math.min(depth, this.simplifiedTrajectory.length)

        const trajectoryPoint = this.simplifiedTrajectory.getPointAt(depth)
        const normal = this.simplifiedTrajectory.getNormalAt(depth)

        const point = trajectoryPoint.add(normal.multiply(offset))

        point.md = md
        point.vector = normal
        point.invalid = false

        return point
    }

    getSegment(mdStart: number, mdEnd: number, offset: number): TrajectoryPoint[] {
        const mdSampled = this.resampleMd(mdStart, mdEnd)
        return mdSampled.map(md => this.getProjectedPoint(md, offset))
    }

    getProblemSegments(maxOffset: number, offset: number): TrajectoryPoint[][] {
        const problems = this.availableDistances.filter(({ distance }) => distance < maxOffset)
        return problems.map(p => [ this.getProjectedPoint(p.startMd, offset), this.getProjectedPoint(p.endMd, offset) ])
    }

    getTrajectorySegment(mdStart: number, mdEnd: number, offset: number): TrajectoryPoint[] {
        const start = this.getNextPointPosition(mdStart, 'md')
        const end = this.getNextPointPosition(mdEnd, 'md')

        return [
            this.getPoint(mdStart, offset),
            ...this.points.slice(start, end).map(v => this.getPoint(v.md, offset)),
            this.getPoint(mdEnd, offset)
        ]
    }

    getArea(mdStart: number, mdEnd: number, offsetStart: number, offsetEnd: number): TrajectoryPoint[] {
        let rightPath = this.getSegment(mdStart, mdEnd, offsetStart)
        let leftPath = this.getSegment(mdStart, mdEnd, offsetEnd)

        if (this.problems.length > 0) {
            rightPath = rightPath.filter(this.isInValidSegment)
            leftPath = leftPath.filter(this.isInValidSegment)
        }

        return [...rightPath, ...leftPath.reverse()]
    }

    getChannel(points: GammaResistivity[], scale?: ScaleLinear<number, number>): TrajectoryPoint[] {
        return points.map(p => {
            return this.getPoint(p.md, scale ? scale(p.value) : 0)
        })
    }

    private isInValidSegment(point: TrajectoryPoint, i: number, array: TrajectoryPoint[]): boolean {
        return point.invalid === false || (i > 0 && point.invalid === true && array[i - 1].invalid === false)
    }

    getSimplified(): paper.Path {
        return this.simplifiedTrajectory
    }
}
